import { IdArrayShrinker, type ShrunkIdArray } from "utils/idArrayShrinker";
import { SelectionSet } from "utils/selectionSet";

/**
 * Compact an id snapshot for the undo/redo stack.
 *
 * Closures must capture the returned `ShrunkIdArray` only — not a SelectionSet
 * — and call {@link restoreUndoIds} when the callback runs. Hide-all /
 * select-all of a dense 2.86M-id scene is ~357 KB bitmap vs ~11 MB
 * `Uint32Array` (or ~23 MB `number[]`) per stack entry, and the 100-entry cap
 * would otherwise retain both undo and redo copies of the expanded arrays.
 *
 * The shrinker requires sorted distinct ids. A `SelectionSet` is sorted by
 * construction and passes through without copying, as does an already-sorted
 * `Uint32Array`; anything else is copied and sorted. The scan that decides is
 * linear and allocation-free, where the shrink it guards is linear anyway.
 */
export function snapshotUndoIds(ids: Iterable<number>): ShrunkIdArray {
    if (ids instanceof SelectionSet) {
        return IdArrayShrinker.shrink(ids.sortedIds);
    }
    if (ids instanceof Uint32Array) {
        // `findIdsByProperties` answers from a Set and leaves the order unspecified, so
        // the search-driven actions reach here unsorted. Sorting a copy: the caller's
        // array is live state (a highlight/hidden set), not ours to reorder.
        return IdArrayShrinker.shrink(isSortedAscending(ids) ? ids : ids.toSorted());
    }
    const arr = Array.isArray(ids) ? ids.slice() : Array.from(ids);
    arr.sort((a, b) => a - b);
    return IdArrayShrinker.shrink(arr);
}

function isSortedAscending(ids: Uint32Array): boolean {
    for (let i = 1; i < ids.length; i++) {
        if (ids[i - 1] > ids[i]) {
            return false;
        }
    }
    return true;
}

export function restoreUndoIds(snapshot: ShrunkIdArray): SelectionSet {
    return SelectionSet.fromSortedIds(IdArrayShrinker.expand(snapshot));
}
